/* DetailedClinicalExam — Step 2 of the case wizard. Five accordion
 * sections covering §§ 2.3 / 2.4 / 2.6 / 2.7 / 2.11 of the dual-workflow
 * spec.
 *
 * iter97.62 — Refactored from one long form into five collapsible
 * sections (Occlusal & Skeletal · Crowding & Spacing · Posterior
 * Occlusion & Crossbites · Functional Movements · Periodontal). Each
 * accordion surfaces a "X/Y filled" badge in its trigger so doctors
 * can spot which sections need attention without expanding. First
 * accordion (Occlusal & Skeletal) opens by default; the rest start
 * collapsed.
 *
 * All fields remain optional — clinics that don't capture this depth
 * still pass validation. Same UX for both Self-plan and CoE flows,
 * only the top-of-section prompt copy differs.
 */
import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    CaretDown, CaretRight, CheckCircle,
    GitBranch, ArrowsInLineHorizontal, ArrowsHorizontal, Waveform, Pulse,
} from "@phosphor-icons/react";

function Select({ label, value, onChange, options, testid, formatLabel }) {
    return (
        <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {label}
            </Label>
            <select
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-9 mt-1 text-sm rounded-md border border-border bg-background px-2"
                data-testid={testid}
            >
                <option value="">—</option>
                {options.map((o) => (
                    <option key={o} value={o}>
                        {formatLabel ? formatLabel(o) : o.replace(/_/g, " ")}
                    </option>
                ))}
            </select>
        </div>
    );
}

function NumberInput({ label, value, onChange, placeholder, testid, step = "0.1" }) {
    return (
        <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {label}
            </Label>
            <Input
                type="number"
                step={step}
                value={value ?? ""}
                onChange={(e) => {
                    if (e.target.value === "") { onChange(""); return; }
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) onChange(n);
                }}
                placeholder={placeholder}
                className="h-9 mt-1"
                data-testid={testid}
            />
        </div>
    );
}

function Checkbox({ label, checked, onChange, testid }) {
    return (
        <label
            className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer select-none"
            data-testid={`${testid}-label`}
        >
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                className="w-4 h-4 rounded border-input accent-primary"
                data-testid={testid}
            />
            <span>{label}</span>
        </label>
    );
}

/* iter97.62 — Local accordion shell for the clinical-exam sections.
 * Mirrors the visual rhythm of PlanSectionAccordion (compact, neutral
 * card) but lighter-weight since each section is just a field grid.
 *
 * iter97.63 — Exported so the parent StepClinical block (CaseSubmissionPage)
 * can reuse the same accordion treatment for the primary sections
 * (Diagnosis, Treatment scope, Medical history, Bite measurements)
 * sitting above the detailed-exam list. Same visual rhythm end-to-end.
 *
 * iter97.66 — De-ghosted: each accordion accepts an `icon` (React
 * element) and a colour `tone`. The tone drives both the soft-tinted
 * icon avatar at the left and the 3px coloured status rail down the
 * left edge. The rail itself overrides to amber (partial) or emerald
 * (complete) so doctors can scan progress without reading every badge.
 * Hover state adds a 1-px lift + subtle shadow so the row feels live.
 */

/* Tonal palette — each tone maps to an icon-avatar bg/text class. The
 * status rail is computed separately so it always reflects fill state,
 * not category. */
const TONE_CLASSES = {
    slate:  "bg-slate-100   text-slate-700",
    teal:   "bg-teal-50     text-teal-700",
    rose:   "bg-rose-50     text-rose-700",
    violet: "bg-violet-50   text-violet-700",
    indigo: "bg-indigo-50   text-indigo-700",
    amber:  "bg-amber-50    text-amber-700",
    orange: "bg-orange-50   text-orange-700",
    cyan:   "bg-cyan-50     text-cyan-700",
};

export function ExamAccordion({ id, title, sub, filled, total, defaultOpen = false, icon = null, tone = "slate", open: controlledOpen, onOpenChange, nextLabel, onNext, headerAccessory = null, bodyClass = "", children }) {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = (next) => {
        const value = typeof next === "function" ? next(open) : next;
        if (!isControlled) setInternalOpen(value);
        onOpenChange?.(value);
    };
    const isComplete = total > 0 && filled >= total;
    const iconBg = TONE_CLASSES[tone] || TONE_CLASSES.slate;

    return (
        <section
            className="group relative bg-card border-[0.5px] border-border rounded-[12px] overflow-hidden transition-all duration-150 hover:shadow-sm hover:border-foreground/15 scroll-mt-[180px]"
            data-testid={`exam-acc-${id}`}
            data-open={open ? "true" : "false"}
            data-complete={isComplete ? "true" : "false"}
        >
            <button
                type="button"
                onClick={() => setOpen((x) => !x)}
                aria-expanded={open}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none text-left hover:bg-muted/30 transition-colors ${open ? "border-b-[0.5px] border-border" : ""}`}
                data-testid={`exam-acc-${id}-trigger`}
            >
                <div className="flex items-center gap-3 min-w-0">
                    {icon && (
                        <div className={`w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0 ${iconBg}`}>
                            {icon}
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="text-[13.5px] font-semibold leading-tight truncate">{title}</div>
                        {sub && <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight truncate">{sub}</div>}
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {/* iter100.39 — Optional live accessory (thumbnail /
                        chip / mini preview) rendered before the badge.
                        Non-interactive by design — the whole header
                        button toggles the accordion, so nothing inside
                        should consume the click. */}
                    {headerAccessory && (
                        <div
                            className="shrink-0 pointer-events-none"
                            data-testid={`exam-acc-${id}-accessory`}
                        >
                            {headerAccessory}
                        </div>
                    )}
                    {/* iter97.79 — badge is optional. When `total` is undefined
                        the accordion is used outside the form-completion
                        context (e.g. Doctor Profile sections that don't
                        track fills), so we skip rendering the pill entirely. */}
                    {total === undefined ? null : isComplete ? (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800"
                            data-testid={`exam-acc-${id}-badge`}
                        >
                            <CheckCircle size={11} weight="fill" /> Complete
                        </span>
                    ) : (
                        <span
                            className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                filled === 0
                                    ? "bg-muted text-muted-foreground"
                                    : "bg-amber-100 text-amber-900"
                            }`}
                            data-testid={`exam-acc-${id}-badge`}
                        >
                            {filled}/{total} filled
                        </span>
                    )}
                    <CaretDown size={13} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>
            {open && (
                <div className={`px-4 py-3.5 ${bodyClass}`} data-testid={`exam-acc-${id}-body`}>
                    {children}
                    {/* iter97.67 — Per-accordion "Next →" footer button.
                        Guides the doctor through sections sequentially
                        so they don't accidentally hit the sticky step
                        Next button mid-fill. Only renders when the
                        parent supplies `onNext` (last accordion has no
                        next). */}
                    {onNext && (
                        <div className="mt-4 pt-3 border-t border-dashed border-border/60 flex items-center justify-between gap-3">
                            <span className="text-[10.5px] text-muted-foreground leading-tight">
                                More to fill below — keep going to give the lab the full picture.
                            </span>
                            <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); onNext(); }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border border-indigo-200 bg-indigo-50/70 text-indigo-800 hover:bg-indigo-100 hover:border-indigo-300 transition-colors shrink-0"
                                data-testid={`exam-acc-${id}-next`}
                            >
                                Next: {nextLabel}
                                <CaretRight size={11} weight="bold" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

const ANGLE_CLASS_LABEL = (v) => v
    .replace("class_i", "Class I")
    .replace("class_ii", "Class II")
    .replace("class_iii", "Class III")
    .replace("mixed", "Mixed")
    .replace("cusp_to_cusp", "Cusp-to-cusp");

/* iter97.62 — `present`: counts a clinical field as "filled" when it has
 * any meaningful value. Booleans are excluded — checkboxes describe
 * presence/absence of a finding and shouldn't bloat the "filled" badge.
 *
 * iter97.63 — Exported so StepClinical (CaseSubmissionPage) reuses the
 * same fill-count semantics for its primary accordions. */
export const present = (v) => v !== "" && v !== null && v !== undefined && typeof v !== "boolean";

export default function DetailedClinicalExam({ clinical, setClinical, metadata, planningMode, openId, setOpenId, goNext }) {
    const upd = (k, v) => setClinical((c) => ({ ...c, [k]: v }));
    // iter90 — removed the collapse-by-default UX. Per the dual-workflow
    // spec, §§ 2.3 / 2.4 / 2.6 / 2.7 / 2.11 are required for BOTH
    // workflows so we now render the section open. Self-plan doctors
    // see a stronger prompt because they're authoring the plan
    // themselves; CoE doctors see a softer prompt because the planner
    // can fill gaps later.
    const isSelfPlan = planningMode === "doctor";

    // iter97.67 — Controlled single-open accordion behavior. When
    // parent passes openId/setOpenId, each accordion only opens if its
    // id matches openId. Falls back to internal uncontrolled mode if
    // not provided (legacy callsites).
    const isControlled = typeof setOpenId === "function";
    const toggle = (id) => setOpenId?.((cur) => (cur === id ? null : id));
    const advance = (id) => (goNext ? () => goNext(id) : undefined);

    // Field count summary — surfaced in the trigger so doctors know
    // how much detail they've added so far without expanding.
    const TRACKED = [
        "angle_class_right", "angle_class_left", "angle_class_anterior",
        "skeletal_class", "vertical_dimension", "midline_relationship",
        "upper_arch_crowding_mm", "lower_arch_crowding_mm", "crowding_distribution",
        "buccal_occlusion_right", "buccal_occlusion_left",
        "canine_class_right", "canine_class_left",
        "anterior_guidance", "lateral_movement_right", "lateral_movement_left",
        "protrusive_movement",
        "periodontal_health", "plaque_index", "perio_compliance",
        // iter89 — scan_model + cbct_available moved to Step 3 so they
        // no longer count toward the Detailed Exam badge.
    ];
    const filledCount = TRACKED.filter((k) => {
        const v = clinical[k];
        return v !== "" && v !== null && v !== undefined && v !== false;
    }).length;

    const opts = {
        angle: metadata?.angle_classes || ["class_i", "class_ii", "class_iii", "mixed"],
        skeletal: metadata?.skeletal_classes || ["class_i", "class_ii", "class_iii"],
        vertical: metadata?.vertical_dimensions || ["normo", "hyper", "hypo"],
        midline: metadata?.midline_relationships || ["coincident", "shifted_upper_left", "shifted_upper_right", "both_shifted"],
        crowding: metadata?.crowding_distributions || ["anterior", "posterior", "generalised"],
        buccal: metadata?.buccal_occlusions || ["class_i", "class_ii", "class_iii", "cusp_to_cusp"],
        canine: metadata?.canine_classes || ["class_i", "class_ii", "class_iii"],
        guidance: metadata?.anterior_guidances || ["canine", "group", "balanced", "none"],
        functional: metadata?.functional_movements || ["smooth", "interference", "restricted"],
        perioHealth: metadata?.perio_health_levels || ["excellent", "good", "fair", "poor", "critical"],
        plaque: metadata?.plaque_indices || ["none", "mild", "moderate", "severe"],
        perioComp: metadata?.perio_compliance_levels || ["good", "fair", "poor"],
    };

    return (
        <div data-testid="detailed-clinical-exam">
            {/* iter97.67 — Single-column stack. The earlier 2-col grid on
                wide screens made the layout confusing (doctors had to
                eye-scan diagonally between left/right pairs that aren't
                logically related). Plain top-to-bottom flow now. */}
            <div className="space-y-2.5">
                {/* §2.3 Occlusal Classification & Skeletal Pattern */}
                <ExamAccordion
                    id="occlusal"
                    title="Occlusal Classification & Skeletal Pattern"
                    sub="Angle classes, skeletal pattern, vertical dimension, midline"
                    icon={<GitBranch size={17} weight="duotone" />}
                    tone="indigo"
                    open={isControlled ? openId === "occlusal" : undefined}
                    onOpenChange={isControlled ? () => toggle("occlusal") : undefined}
                    nextLabel="Crowding & spacing"
                    onNext={advance("crowding")}
                    filled={[
                        clinical.angle_class_right, clinical.angle_class_left, clinical.angle_class_anterior,
                        clinical.skeletal_class, clinical.vertical_dimension, clinical.midline_relationship,
                    ].filter(present).length}
                    total={6}
                    defaultOpen={false}
                >
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <Select
                            label="Angle (right)"
                            value={clinical.angle_class_right}
                            onChange={(v) => upd("angle_class_right", v)}
                            options={opts.angle}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-angle-right"
                        />
                        <Select
                            label="Angle (left)"
                            value={clinical.angle_class_left}
                            onChange={(v) => upd("angle_class_left", v)}
                            options={opts.angle}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-angle-left"
                        />
                        <Select
                            label="Angle (anterior)"
                            value={clinical.angle_class_anterior}
                            onChange={(v) => upd("angle_class_anterior", v)}
                            options={opts.angle}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-angle-anterior"
                        />
                        <Select
                            label="Skeletal class"
                            value={clinical.skeletal_class}
                            onChange={(v) => upd("skeletal_class", v)}
                            options={opts.skeletal}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-skeletal-class"
                        />
                        <Select
                            label="Vertical dimension"
                            value={clinical.vertical_dimension}
                            onChange={(v) => upd("vertical_dimension", v)}
                            options={opts.vertical}
                            testid="exam-vertical-dimension"
                        />
                        <Select
                            label="Midline"
                            value={clinical.midline_relationship}
                            onChange={(v) => upd("midline_relationship", v)}
                            options={opts.midline}
                            testid="exam-midline"
                        />
                    </div>
                    {clinical.midline_relationship && clinical.midline_relationship !== "coincident" && (
                        <div className="mt-3 max-w-[200px]">
                            <NumberInput
                                label="Midline shift (mm)"
                                value={clinical.midline_shift_mm}
                                onChange={(v) => upd("midline_shift_mm", v)}
                                placeholder="e.g. 1.5"
                                testid="exam-midline-shift"
                            />
                        </div>
                    )}
                </ExamAccordion>

                {/* §2.4 Crowding & Spacing */}
                <ExamAccordion
                    id="crowding"
                    title="Crowding & Spacing Analysis"
                    sub="Per-arch crowding, distribution, spacing location"
                    icon={<ArrowsInLineHorizontal size={17} weight="duotone" />}
                    tone="amber"
                    open={isControlled ? openId === "crowding" : undefined}
                    onOpenChange={isControlled ? () => toggle("crowding") : undefined}
                    nextLabel="Posterior occlusion"
                    onNext={advance("posterior")}
                    filled={[
                        clinical.upper_arch_crowding_mm,
                        clinical.lower_arch_crowding_mm,
                        clinical.crowding_distribution,
                        clinical.spacing_location,
                    ].filter(present).length}
                    total={4}
                >
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <NumberInput
                            label="Upper arch crowding (mm)"
                            value={clinical.upper_arch_crowding_mm}
                            onChange={(v) => upd("upper_arch_crowding_mm", v)}
                            placeholder="negative = spacing"
                            testid="exam-upper-crowding"
                        />
                        <NumberInput
                            label="Lower arch crowding (mm)"
                            value={clinical.lower_arch_crowding_mm}
                            onChange={(v) => upd("lower_arch_crowding_mm", v)}
                            placeholder="negative = spacing"
                            testid="exam-lower-crowding"
                        />
                        <Select
                            label="Distribution"
                            value={clinical.crowding_distribution}
                            onChange={(v) => upd("crowding_distribution", v)}
                            options={opts.crowding}
                            testid="exam-crowding-distribution"
                        />
                    </div>
                    <div className="mt-3">
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            Spacing location (if applicable)
                        </Label>
                        <Input
                            value={clinical.spacing_location || ""}
                            onChange={(e) => upd("spacing_location", e.target.value)}
                            placeholder="e.g. Diastema 11–21, generalised lower spacing"
                            className="h-9 mt-1"
                            data-testid="exam-spacing-location"
                        />
                    </div>
                </ExamAccordion>

                {/* §2.6 Posterior Occlusion & Crossbites */}
                <ExamAccordion
                    id="posterior"
                    title="Posterior Occlusion & Crossbites"
                    sub="Buccal occlusion, canine class, crossbite findings"
                    icon={<ArrowsHorizontal size={17} weight="duotone" />}
                    tone="orange"
                    open={isControlled ? openId === "posterior" : undefined}
                    onOpenChange={isControlled ? () => toggle("posterior") : undefined}
                    nextLabel="Functional movements"
                    onNext={advance("functional")}
                    filled={[
                        clinical.buccal_occlusion_right, clinical.buccal_occlusion_left,
                        clinical.canine_class_right, clinical.canine_class_left,
                    ].filter(present).length}
                    total={4}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <Select
                            label="Buccal occlusion (right)"
                            value={clinical.buccal_occlusion_right}
                            onChange={(v) => upd("buccal_occlusion_right", v)}
                            options={opts.buccal}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-buccal-right"
                        />
                        <Select
                            label="Buccal occlusion (left)"
                            value={clinical.buccal_occlusion_left}
                            onChange={(v) => upd("buccal_occlusion_left", v)}
                            options={opts.buccal}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-buccal-left"
                        />
                        <Select
                            label="Canine class (right)"
                            value={clinical.canine_class_right}
                            onChange={(v) => upd("canine_class_right", v)}
                            options={opts.canine}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-canine-right"
                        />
                        <Select
                            label="Canine class (left)"
                            value={clinical.canine_class_left}
                            onChange={(v) => upd("canine_class_left", v)}
                            options={opts.canine}
                            formatLabel={ANGLE_CLASS_LABEL}
                            testid="exam-canine-left"
                        />
                    </div>
                    <div className="mt-3 space-y-2">
                        <Checkbox
                            label="Posterior crossbite present"
                            checked={clinical.posterior_crossbite}
                            onChange={(v) => upd("posterior_crossbite", v)}
                            testid="exam-posterior-crossbite"
                        />
                        {clinical.posterior_crossbite && (
                            <div>
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                    Crossbite teeth (FDI)
                                </Label>
                                <Input
                                    value={clinical.posterior_crossbite_location || ""}
                                    onChange={(e) => upd("posterior_crossbite_location", e.target.value)}
                                    placeholder="e.g. 16, 26"
                                    className="h-9 mt-1"
                                    data-testid="exam-posterior-crossbite-location"
                                />
                            </div>
                        )}
                        <Checkbox
                            label="Anterior crossbite present"
                            checked={clinical.anterior_crossbite}
                            onChange={(v) => upd("anterior_crossbite", v)}
                            testid="exam-anterior-crossbite"
                        />
                    </div>
                </ExamAccordion>

                {/* §2.7 Bite Characteristics & Functional Movements */}
                <ExamAccordion
                    id="functional"
                    title="Functional Movements"
                    sub="Anterior guidance, lateral & protrusive movements, functional shift"
                    icon={<Waveform size={17} weight="duotone" />}
                    tone="cyan"
                    open={isControlled ? openId === "functional" : undefined}
                    onOpenChange={isControlled ? () => toggle("functional") : undefined}
                    nextLabel="Periodontal status"
                    onNext={advance("periodontal")}
                    filled={[
                        clinical.anterior_guidance,
                        clinical.lateral_movement_right, clinical.lateral_movement_left,
                        clinical.protrusive_movement,
                    ].filter(present).length}
                    total={4}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <Select
                            label="Anterior guidance"
                            value={clinical.anterior_guidance}
                            onChange={(v) => upd("anterior_guidance", v)}
                            options={opts.guidance}
                            testid="exam-anterior-guidance"
                        />
                        <div></div>
                        <Select
                            label="Lateral movement (right)"
                            value={clinical.lateral_movement_right}
                            onChange={(v) => upd("lateral_movement_right", v)}
                            options={opts.functional}
                            testid="exam-lateral-right"
                        />
                        <Select
                            label="Lateral movement (left)"
                            value={clinical.lateral_movement_left}
                            onChange={(v) => upd("lateral_movement_left", v)}
                            options={opts.functional}
                            testid="exam-lateral-left"
                        />
                        <Select
                            label="Protrusive movement"
                            value={clinical.protrusive_movement}
                            onChange={(v) => upd("protrusive_movement", v)}
                            options={opts.functional}
                            testid="exam-protrusive"
                        />
                    </div>
                    <div className="mt-3">
                        <Checkbox
                            label="Functional shift noted (e.g. mandibular shift on closure)"
                            checked={clinical.functional_shift}
                            onChange={(v) => upd("functional_shift", v)}
                            testid="exam-functional-shift"
                        />
                    </div>
                </ExamAccordion>

                {/* §2.11 Periodontal Status (Structured) */}
                <ExamAccordion
                    id="periodontal"
                    title="Periodontal Status"
                    sub="Health, plaque index, compliance, gingival recession"
                    icon={<Pulse size={17} weight="duotone" />}
                    tone="rose"
                    open={isControlled ? openId === "periodontal" : undefined}
                    onOpenChange={isControlled ? () => toggle("periodontal") : undefined}
                    filled={[
                        clinical.periodontal_health,
                        clinical.plaque_index,
                        clinical.perio_compliance,
                    ].filter(present).length}
                    total={3}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <Select
                            label="Health"
                            value={clinical.periodontal_health}
                            onChange={(v) => upd("periodontal_health", v)}
                            options={opts.perioHealth}
                            testid="exam-perio-health"
                        />
                        <Select
                            label="Plaque index"
                            value={clinical.plaque_index}
                            onChange={(v) => upd("plaque_index", v)}
                            options={opts.plaque}
                            testid="exam-plaque-index"
                        />
                        <Select
                            label="Compliance"
                            value={clinical.perio_compliance}
                            onChange={(v) => upd("perio_compliance", v)}
                            options={opts.perioComp}
                            testid="exam-perio-compliance"
                        />
                    </div>
                    <div className="mt-3">
                        <Checkbox
                            label="Gingival recession present"
                            checked={clinical.gingival_recession}
                            onChange={(v) => upd("gingival_recession", v)}
                            testid="exam-gingival-recession"
                        />
                    </div>
                </ExamAccordion>

                {/* iter89 — scan extras (scanner model, CBCT
                    availability) moved to Step 3 (Scans & Photos)
                    alongside the actual scan file uploads. */}
            </div>
        </div>
    );
}
